import { computed, signal } from '@preact/signals'
import raw from '../../../programs/programs.json'
import ruTexts from '../../../i18n/programs/ru.json'
import { lang, onLangLoad, type Lang } from '../i18n/index.js'
import { ageWeeks } from './profile.js'
import type { ChildProfile, Phase, PhaseType, SleepNorm, WakeProgram } from './types.js'

/**
 * Программы таймера собираются из двух файлов (план `docs/i18n-plan.md`, 6.6):
 * числа и структура — `programs/programs.json`, тот же файл читает Kotlin;
 * тексты — `i18n/programs/<lang>.json`, Kotlin получает их через сгенерированные
 * `strings_programs.xml` (`web/scripts/gen-android-strings.mjs`).
 *
 * Русские тексты в сборке всегда, остальные подгружаются по требованию через
 * `onLangLoad`: словарь дожидается их до переключения языка. Собранные программы
 * читают сигнал языка, поэтому экраны перерисовываются при его смене.
 *
 * Расхождение чисел между слоями ловит [fingerprint]: значение зафиксировано в тестах обеих сторон.
 */

interface RawPhase {
  minutes: number
  type: string
  flexible?: boolean
  showTiredSigns?: boolean
}

interface RawProgram {
  id: string
  ageFromWeeks: number
  ageToWeeks: number
  windowMin: number[]
  variantOf?: string
  sleep?: { perDayHours: number[]; napMinutes: number[]; wakePushFromWeeks?: number }
  phases: RawPhase[]
}

/** Тексты одной программы из `i18n/programs/<lang>.json`: подписи и фазы по порядку. */
export interface ProgramTexts {
  ageLabel: string
  tiredSigns: string
  phases: Array<{ title: string; tip: string }>
}

type TextsFile = Record<string, ProgramTexts>

/** Нормы на случай программы без поля `sleep` (заготовки в тестах): как у самых маленьких. */
export const DEFAULT_SLEEP: SleepNorm = { perDayHours: [14, 17], napMinutes: [45, 120] }

/** Нормы сна программы; у программ из файла они есть всегда. */
export function sleepNormOf(program: WakeProgram): SleepNorm {
  return program.sleep ?? DEFAULT_SLEEP
}

function toSleep(s: NonNullable<RawProgram['sleep']>): SleepNorm {
  return {
    perDayHours: [s.perDayHours[0], s.perDayHours[1]] as const,
    napMinutes: [s.napMinutes[0], s.napMinutes[1]] as const,
    ...(s.wakePushFromWeeks === undefined ? {} : { wakePushFromWeeks: s.wakePushFromWeeks }),
  }
}

const file = raw as { version: number; phaseColors: Record<string, string>; programs: RawProgram[] }

if (file.version !== 1) throw new Error(`programs.json версии ${file.version}, поддерживается 1`)

// ---- тексты по языкам ----

const ЭТАЛОН = ruTexts as TextsFile

// Эталон обязан покрывать каждую программу и каждую фазу: иначе на экране будет пусто,
// и заметят это не тесты, а человек. Падаем при загрузке модуля.
for (const p of file.programs) {
  const texts = ЭТАЛОН[p.id]
  if (texts === undefined) throw new Error(`i18n/programs/ru.json: нет текстов программы ${p.id}`)
  if (texts.phases.length !== p.phases.length) {
    throw new Error(`i18n/programs/ru.json: у ${p.id} ${texts.phases.length} фаз, в programs.json ${p.phases.length}`)
  }
}

const файлы = import.meta.glob<{ default: TextsFile }>('../../../i18n/programs/*.json')

/** Загруженные тексты по языкам. Сигнал: программы пересобираются, когда файл доехал. */
const тексты = signal<ReadonlyMap<Lang, TextsFile>>(new Map([['ru', ЭТАЛОН]]))

async function загрузить(next: Lang): Promise<void> {
  if (тексты.value.has(next)) return
  const loader = файлы[`../../../i18n/programs/${next}.json`]
  // Файла для языка ещё нет: тексты остаются русскими, как у словаря
  if (loader === undefined) return
  const module = await loader()
  тексты.value = new Map(тексты.value).set(next, module.default)
}

onLangLoad(async (next) => {
  await загрузить(next)
  // Английский запасной для всех: держим его под рукой, как только он понадобился
  if (next !== 'en' && next !== 'ru') void загрузить('en')
})

/** Тексты программы на текущем языке; нет перевода — английский, нет и его — русский. */
function текстыПрограммы(id: string): ProgramTexts {
  const map = тексты.value
  return map.get(lang.value)?.[id] ?? map.get('en')?.[id] ?? (ЭТАЛОН[id] as ProgramTexts)
}

// ---- сборка программ ----

function toPhase(p: RawPhase, text: { title: string; tip: string } | undefined): Phase {
  return {
    title: text?.title ?? '',
    minutes: p.minutes,
    type: p.type as PhaseType,
    tip: text?.tip ?? '',
    flexible: p.flexible ?? true,
    showTiredSigns: p.showTiredSigns ?? false,
  }
}

function toProgram(p: RawProgram): WakeProgram {
  const texts = текстыПрограммы(p.id)
  return {
    id: p.id,
    ageLabel: texts.ageLabel,
    ageFromWeeks: p.ageFromWeeks,
    ageToWeeks: p.ageToWeeks,
    windowMin: [p.windowMin[0], p.windowMin[1]] as const,
    tiredSigns: texts.tiredSigns,
    ...(p.variantOf === undefined ? {} : { variantOf: p.variantOf }),
    ...(p.sleep === undefined ? {} : { sleep: toSleep(p.sleep) }),
    phases: p.phases.map((f, i) => toPhase(f, texts.phases[i])),
  }
}

/**
 * Программы на текущем языке. Пересобираются только при смене языка или приходе
 * текстов, поэтому между вызовами объекты одни и те же — сравнения по ссылке в
 * экранах не ломаются.
 */
const собранные = computed(() => {
  const parsed = file.programs.map(toProgram)
  return {
    parsed,
    all: parsed.filter((p) => p.variantOf === undefined),
    variants: parsed.filter((p) => p.variantOf !== undefined),
  }
})

/** Программы по возрастам, по порядку. Варианты сюда не входят. */
export function all(): WakeProgram[] {
  return собранные.value.all
}

/** Варианты программ, например «2–3 месяца · при газиках». */
export function variants(): WakeProgram[] {
  return собранные.value.variants
}

/** Цвет каждого типа фазы, тот же, что в Kotlin-перечислении `PhaseType`. */
export const phaseColors: Record<PhaseType, string> = file.phaseColors as Record<PhaseType, string>

export function byId(id: string): WakeProgram | undefined {
  return собранные.value.parsed.find((p) => p.id === id)
}

/** Программа по возрасту в неделях; старше последнего диапазона — последняя программа. */
export function byAgeWeeks(weeks: number): WakeProgram {
  const list = all()
  return list.find((p) => weeks >= p.ageFromWeeks && weeks < p.ageToWeeks) ?? (list[list.length - 1] as WakeProgram)
}

/** Программа для профиля: по возрасту, с учётом варианта «при газиках» (пока только 2–3 месяца). */
export function forProfile(profile: ChildProfile, now: number): WakeProgram {
  const base = byAgeWeeks(ageWeeks(profile, now))
  if (profile.gasVariant && base.id === 'm2_3') {
    const gas = byId('m2_3_gas')
    if (gas) return gas
  }
  return base
}

/** Программа без профиля (2–3 месяца) на текущем языке. */
export function defaultProgram(): WakeProgram {
  return byId('m2_3') as WakeProgram
}

/**
 * Контрольная строка содержимого: версия, число программ, число фаз и хеш FNV-1a
 * по числам, типам и флагам. Тексты в неё не входят: они живут отдельно и по языкам.
 * Точно та же функция есть в Kotlin (`Programs.fingerprint`), значения обязаны совпадать:
 * FNV-1a считается по UTF-16 code units, которые в Kotlin и JS одинаковы.
 */
export function fingerprint(): string {
  let text = ''
  for (const p of file.programs) {
    const s = p.sleep === undefined ? DEFAULT_SLEEP : toSleep(p.sleep)
    text += `${p.id}|${p.ageFromWeeks}-${p.ageToWeeks}|${p.windowMin[0]}-${p.windowMin[1]}|`
    text += `${s.perDayHours[0]}-${s.perDayHours[1]}|${s.napMinutes[0]}-${s.napMinutes[1]}|${s.wakePushFromWeeks ?? 0}|`
    for (const f of p.phases) {
      text += `${f.minutes}:${f.type}:${(f.flexible ?? true) ? 1 : 0}${(f.showTiredSigns ?? false) ? 1 : 0};`
    }
    text += '\n'
  }
  let h = 0x811c9dc5 | 0
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193)
  const hex = (h >>> 0).toString(16).padStart(8, '0')
  const phases = file.programs.reduce((sum, p) => sum + p.phases.length, 0)
  return `v2:${file.programs.length}:${phases}:${hex}`
}
